/**
 * Volet de synthèse — Registre d'icônes lucide (clé string → composant).
 *
 * Le backend nomme des *rôles* d'icône (« Building2 », « MapPin », « Coins »…) ;
 * ce registre les résout en composants lucide côté front. Liste FIGÉE (§3 norm) :
 * header par type + rôles de section mutualisés + glossaire état/badge/action.
 *
 * Règle d'or : lucide wireframe uniquement ; la couleur est portée par le `tone`,
 * jamais par l'icône. Finance = `Euro`/`Coins`, JAMAIS `DollarSign`/`Receipt`
 * (piège dollar lucide). `handshake` absent de v0.303 → `UsersRound`.
 */

import {
    Accessibility,
    Activity,
    AlertTriangle,
    BadgeCheck,
    BadgeEuro,
    Ban,
    BedDouble,
    Boxes,
    Briefcase,
    Building2,
    CalendarClock,
    CalendarDays,
    CalendarX,
    Car,
    CheckCircle2,
    Circle,
    CircleSlash,
    ClipboardCheck,
    ClipboardList,
    Clock,
    Coffee,
    Coins,
    Compass,
    ConciergeBell,
    Contact,
    DoorOpen,
    Download,
    Euro,
    ExternalLink,
    Eye,
    EyeOff,
    Factory,
    FileBadge,
    FilePlus,
    FileText,
    Globe,
    GraduationCap,
    Hash,
    History,
    Hotel,
    Landmark,
    ListChecks,
    Mail,
    MapPin,
    MessageSquare,
    MonitorPlay,
    Package,
    ParkingCircle,
    PauseCircle,
    Percent,
    Phone,
    Pin,
    Salad,
    ScanBarcode,
    ScrollText,
    Search,
    ShieldCheck,
    SlidersHorizontal,
    Sparkles,
    Star,
    StickyNote,
    Store,
    Timer,
    Tag,
    Target,
    TrendingUp,
    Truck,
    Unlink,
    User,
    UserX,
    Users,
    UsersRound,
    UtensilsCrossed,
    Wifi,
    type LucideIcon,
} from 'lucide-react'

/** Résolution string → lucide (clé PascalCase émise par le backend). */
const SUMMARY_ICONS: Record<string, LucideIcon> = {
    // Header par type
    Building2,
    Landmark,
    Users,
    GraduationCap,
    Package,
    Coins,
    Hotel,
    BedDouble,
    UtensilsCrossed,
    MapPin,
    Factory,
    // Devis : € en pastille. `Receipt` est PROSCRIT (son tracé porte un `$`) et
    // `Euro` seul désigne déjà les Frais — cf. l'en-tête de ce fichier.
    BadgeEuro,
    ScrollText,
    // Rôles de section
    Contact,
    FileBadge,
    FileText,
    ListChecks,
    Briefcase,
    UsersRound,
    ShieldCheck,
    ClipboardList,
    BadgeCheck,
    Euro,
    Boxes,
    Truck,
    SlidersHorizontal,
    User,
    ConciergeBell,
    Salad,
    DoorOpen,
    // État / badge / action
    CheckCircle2,
    Circle,
    PauseCircle,
    CircleSlash,
    AlertTriangle,
    Ban,
    EyeOff,
    Accessibility,
    Unlink,
    Star,
    UserX,
    Mail,
    FilePlus,
    // Qualité / pédagogie / pièces — catégories ajoutées au volet le 2026-08-11.
    // ⚠️ Une icône nommée par le backend mais ABSENTE d'ici ne rend RIEN, sans
    // erreur : le gardien `summaryIcons.test.ts` compare les deux listes.
    Activity,
    ClipboardCheck,
    Clock,
    Globe,
    MessageSquare,
    MonitorPlay,
    Search,
    Sparkles,
    Target,
    CalendarDays,
    // 30_LOG / RSC — l'échéance d'une réservation dans le volet du matériel.
    // ⚠️ Ce registre est FIGÉ : une icône nommée par le serveur mais absente
    // d'ICI ne rend RIEN — pas d'erreur, juste un vide. C'est le gardien
    // `test_summary_builders_contract` qui l'a vue manquer, une fois seulement
    // qu'il a cessé de lire une liste de fichiers écrite à la main.
    CalendarClock,
    History,
    // 30_LOG / RSC-008 — la FIN d'un contrat de location, et le délai de
    // livraison d'une commande. Deux rôles distincts d'une date : l'un est un
    // terme, l'autre une durée.
    CalendarX,
    Timer,
    // Matériel de formation (30_LOG / RSC) — rôles de LIGNE : numéro
    // d'inventaire, numéro de série, précision de rangement.
    Hash,
    ScanBarcode,
    StickyNote,
    ExternalLink,
    Phone,
    TrendingUp,
    Percent,
    Tag,
    // 30_LOG / RSC-006 — « Verifications a la charge du prestataire ». La
    // pastille de la FICHE porte deja ce trace (`ResourceComplianceBadge`) :
    // le volet la reprend mot pour mot, il doit la reprendre trait pour trait.
    // ⚠️ Sans cette entree, le serveur nomme un role que ce registre ignore, et
    // la pastille sort SANS icone — aucune erreur, juste un vide.
    Store,
    Wifi,
    Coffee,
    ParkingCircle,
    Pin,
    // Ligne de `dl` : icône PAR LIGNE, comme la carte de survol (PO 2026-08-11).
    // `Compass` (rayon d'intervention) et `Car` (barème km, véhicule) viennent
    // de `InstructorsMap` — la carte étant la présentation de référence, on
    // reprend SES icônes plutôt que d'en réinventer d'équivalentes.
    Car,
    Compass,
    // Actions de FICHIER, absorbées de l'aperçu ⌘K le 2026-08-11 : lecteur plein
    // écran (`Eye`) et téléchargement (`Download`).
    Download,
    Eye,
}

/** Composant lucide pour une clé (ou `null` si inconnue → l'appelant omet l'icône). */
export function summaryIcon(key: string | null | undefined): LucideIcon | null {
    if (!key) return null
    return SUMMARY_ICONS[key] ?? null
}
